// HomePage mixins for shared functionality
export const provinceFilterMixin = {
    methods: {
        /**
         * Split province name to remove spaces for URL routing
         * @param {string} province - Province name
         * @returns {string} - Province name without spaces
         */
        splitProvince(province) {
            if (!province) return '';
            return province.split(' ').join('');
        },

        /**
         * Get order amount for a specific province
         * @param {number} provinceId - Province ID
         * @returns {number} - Order amount
         */
        getOrderAmount(provinceId) {
            try {
                const orderCountByProvince = this.orderCountEachProvince.find(
                    (province) => province.province_id == provinceId
                );
                return orderCountByProvince && orderCountByProvince.order_amount || 0;
            } catch (error) {
                console.warn('Error getting order amount:', error);
                return 0;
            }
        },

        /**
         * Check if province has permissions based on user role
         * @param {Object} province - Province object
         * @returns {boolean} - Whether user has permission
         */
        hasProvincePermission(province) {
            const userProvinces = this.permission && this.permission.province || [];

            // If user has 'all' permission or empty array (all access)
            if (userProvinces.length === 0 || userProvinces[0] === 'all') {
                return true;
            }

            // Check if user has permission for this specific province
            return userProvinces.some(
                (userProvince) =>
                    this.splitProvince(province.province_name_en).toLowerCase() === userProvince
            );
        },

        /**
         * Filter provinces based on search query
         * @param {Array} provinces - Array of provinces
         * @param {string} searchQuery - Search query
         * @returns {Array} - Filtered provinces
         */
        filterProvincesBySearch(provinces, searchQuery) {
            if (!searchQuery) return provinces;

            const lowerQuery = searchQuery.toLowerCase();
            return provinces.filter(province =>
                province.province_name_en.toLowerCase().includes(lowerQuery) ||
                province.province_name_th.toLowerCase().includes(lowerQuery)
            );
        },

        /**
         * Sort provinces with pinned ones first
         * @param {Array} provinces - Array of provinces
         * @param {Array} pinnedProvinceIds - Array of pinned province IDs
         * @returns {Array} - Sorted provinces
         */
        sortProvincesByPinned(provinces, pinnedProvinceIds) {
            return [...provinces].sort((a, b) => {
                const aIsPinned = pinnedProvinceIds.includes(a.province_id);
                const bIsPinned = pinnedProvinceIds.includes(b.province_id);

                if (aIsPinned && !bIsPinned) return -1;
                if (!aIsPinned && bIsPinned) return 1;
                return 0;
            });
        }
    }
};

export const localStorageMixin = {
    methods: {
        /**
         * Save data to localStorage with error handling
         * @param {string} key - Storage key
         * @param {*} data - Data to save
         */
        saveToLocalStorage(key, data) {
            try {
                localStorage.setItem(key, JSON.stringify(data));
            } catch (error) {
                console.error(`Error saving ${key} to localStorage:`, error);
            }
        },

        /**
         * Load data from localStorage with error handling
         * @param {string} key - Storage key
         * @param {*} defaultValue - Default value if not found
         * @returns {*} - Loaded data or default value
         */
        loadFromLocalStorage(key, defaultValue = null) {
            try {
                const stored = localStorage.getItem(key);
                return stored ? JSON.parse(stored) : defaultValue;
            } catch (error) {
                console.error(`Error loading ${key} from localStorage:`, error);
                return defaultValue;
            }
        }
    }
};

export const timeFormattingMixin = {
    methods: {
        /**
         * Format time to HH:MM format
         * @param {Date} date - Date object
         * @returns {string} - Formatted time
         */
        formatTime(date = new Date()) {
            return date.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
            });
        },

        /**
         * Get current formatted time
         * @returns {string} - Current time in HH:MM format
         */
        getCurrentTime() {
            return this.formatTime();
        }
    }
};
